import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  CurrencyEuroIcon,
  DocumentIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PhotoIcon,
  TagIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { ModalActions, ModalShell } from './modals/core';

interface LinkedBooking {
  buchung_id: number;
  konto_id: number;
  buchungstag: string;
  name_zahlungsbeteiligter: string;
  buchungstext: string;
  verwendungszweck: string;
  betrag: number;
  saldo: number;
  von_konto_id: number | null;
  nach_konto_id: number | null;
}

interface Document {
  id: number;
  name: string;
  file_path?: string;
  created_at: string;
  member_id?: string;
  member_name?: string;
  size: number;
  tag?: string;
  ai_comment?: string;
  invoice_amount?: number;
  currency?: string;
  linked_bookings?: LinkedBooking[];
  is_linked?: number;
}

interface InvoiceMatcherProps {
  onFilesSelected: (files: Document[]) => void | Promise<void>;
  initialAmount?: number;
  preSelectedDocuments?: number[];
  onClose: () => void;
}

type DocumentFilter = 'available' | 'all';

const InvoiceMatcher: React.FC<InvoiceMatcherProps> = ({
  onFilesSelected,
  initialAmount = 0,
  preSelectedDocuments = [],
  onClose,
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<DocumentFilter>('available');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const selectionInitialized = useRef(false);

  const targetAmount = Math.abs(Number(initialAmount) || 0);
  const preSelectedIds = useMemo(
    () => new Set(preSelectedDocuments),
    [preSelectedDocuments],
  );

  const isLinked = useCallback(
    (doc: Document) => Boolean(doc.is_linked || doc.linked_bookings?.length),
    [],
  );

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/api/documents');
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error('Fehler beim Laden der Dokumente:', err);
      setError(err?.response?.data?.message || err?.message || 'Dokumente konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    if (selectionInitialized.current || documents.length === 0) return;

    const validPreSelected = preSelectedDocuments.filter((id) =>
      documents.some((doc) => doc.id === id),
    );
    setSelectedDocuments(new Set(validPreSelected));
    selectionInitialized.current = true;
  }, [documents, preSelectedDocuments]);

  const availableCount = useMemo(
    () => documents.filter((doc) => !isLinked(doc)).length,
    [documents, isLinked],
  );

  const filteredDocs = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('de-DE');

    return documents
      .filter((doc) => {
        if (filter === 'available' && isLinked(doc) && !preSelectedIds.has(doc.id)) {
          return false;
        }

        if (!normalizedSearch) return true;

        return [doc.name, doc.member_name, doc.tag, doc.ai_comment]
          .filter(Boolean)
          .some((value) => String(value).toLocaleLowerCase('de-DE').includes(normalizedSearch));
      })
      .sort((a, b) => {
        const aCurrent = preSelectedIds.has(a.id) ? 1 : 0;
        const bCurrent = preSelectedIds.has(b.id) ? 1 : 0;
        if (aCurrent !== bCurrent) return bCurrent - aCurrent;

        const aMatches = Math.abs(Number(a.invoice_amount) - targetAmount) < 0.01 ? 1 : 0;
        const bMatches = Math.abs(Number(b.invoice_amount) - targetAmount) < 0.01 ? 1 : 0;
        if (aMatches !== bMatches) return bMatches - aMatches;

        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
  }, [documents, filter, isLinked, preSelectedIds, searchTerm, targetAmount]);

  const selectedDocs = useMemo(
    () => documents.filter((doc) => selectedDocuments.has(doc.id)),
    [documents, selectedDocuments],
  );

  const selectedAmount = useMemo(
    () => selectedDocs.reduce((sum, doc) => sum + (Number(doc.invoice_amount) || 0), 0),
    [selectedDocs],
  );

  const difference = targetAmount - selectedAmount;
  const amountMatches = selectedDocs.length > 0 && Math.abs(difference) < 0.01;
  const allVisibleSelected =
    filteredDocs.length > 0 && filteredDocs.every((doc) => selectedDocuments.has(doc.id));

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return 'Datum unbekannt';
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes < 1) return null;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} MB`;
  };

  const documentType = (doc: Document) => {
    const source = doc.file_path || doc.name;
    const extension = source.split('.').pop()?.toUpperCase();
    return extension && extension.length <= 5 ? extension : 'DATEI';
  };

  const getDocumentIcon = (doc: Document) => {
    const source = doc.file_path || doc.name;
    if (/\.pdf$/i.test(source)) {
      return <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />;
    }
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(source)) {
      return <PhotoIcon className="h-5 w-5" aria-hidden="true" />;
    }
    return <DocumentIcon className="h-5 w-5" aria-hidden="true" />;
  };

  const toggleDocument = (documentId: number) => {
    setActionError(null);
    setSelectedDocuments((current) => {
      const next = new Set(current);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  };

  const toggleVisibleDocuments = () => {
    setActionError(null);
    setSelectedDocuments((current) => {
      const next = new Set(current);
      if (allVisibleSelected) filteredDocs.forEach((doc) => next.delete(doc.id));
      else filteredDocs.forEach((doc) => next.add(doc.id));
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedDocs.length === 0 || saving) return;

    try {
      setSaving(true);
      setActionError(null);
      await onFilesSelected(selectedDocs);
      onClose();
    } catch (err: any) {
      console.error('Fehler beim Verknüpfen der Dokumente:', err);
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Die Dokumente konnten nicht verknüpft werden. Bitte erneut versuchen.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      open
      onClose={onClose}
      variant="choice"
      size="xl"
      mobilePresentation="fullscreen"
      closeOnBackdrop={!saving}
      closeOnEscape={!saving}
      hideCloseButton={saving}
      icon={LinkIcon}
      title="Dokumente verknüpfen"
      subtitle="Belege auswählen und der Buchung zuordnen"
      bodyClassName="p-0"
      footer={
        <ModalActions
          extra={
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-semibold text-gray-900 dark:text-white">
                {selectedDocs.length}
              </span>{' '}
              {selectedDocs.length === 1 ? 'Dokument' : 'Dokumente'} ausgewählt
              {selectedDocs.length > 0 && (
                <span className={amountMatches ? 'text-success-700 dark:text-success-400' : ''}>
                  {' '}· Differenz {formatCurrency(difference)}
                </span>
              )}
            </p>
          }
          secondary={{ label: 'Abbrechen', onClick: onClose, disabled: saving }}
          primary={{
            label:
              selectedDocs.length === 1
                ? 'Dokument verknüpfen'
                : `${selectedDocs.length} Dokumente verknüpfen`,
            onClick: handleConfirm,
            disabled: selectedDocs.length === 0 || saving,
            loading: saving,
            icon: <LinkIcon className="h-4 w-4" aria-hidden="true" />,
          }}
        />
      }
    >
      <div className="flex h-full min-h-[560px] flex-col sm:min-h-0 sm:h-[64vh]">
        <section className="grid flex-shrink-0 grid-cols-1 gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/35 sm:grid-cols-3 sm:gap-3 sm:px-5">
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 dark:border-gray-700 dark:bg-gray-800">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Buchung</p>
              <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                {formatCurrency(targetAmount)}
              </p>
            </div>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/25 dark:text-primary-400">
              <CurrencyEuroIcon className="h-5 w-5" aria-hidden="true" />
            </span>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 dark:border-gray-700 dark:bg-gray-800">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Ausgewählt</p>
              <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                {formatCurrency(selectedAmount)}
              </p>
            </div>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-info-50 text-info-600 dark:bg-info-900/25 dark:text-info-400">
              <PaperClipIcon className="h-5 w-5" aria-hidden="true" />
            </span>
          </div>

          <div
            className={`flex items-center justify-between rounded-lg border px-3.5 py-2.5 ${
              amountMatches
                ? 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
            }`}
          >
            <div>
              <p className={`text-[11px] font-bold uppercase tracking-wider ${amountMatches ? 'text-success-700 dark:text-success-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {amountMatches ? 'Betrag stimmt' : 'Differenz'}
              </p>
              <p className={`mt-0.5 text-lg font-bold tabular-nums ${amountMatches ? 'text-success-700 dark:text-success-400' : 'text-gray-900 dark:text-white'}`}>
                {formatCurrency(difference)}
              </p>
            </div>
            <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${amountMatches ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
              {amountMatches ? <CheckCircleIcon className="h-5 w-5" aria-hidden="true" /> : <CurrencyEuroIcon className="h-5 w-5" aria-hidden="true" />}
            </span>
          </div>
        </section>

        <section className="flex flex-shrink-0 flex-col gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Nach Dateiname, Mitglied, Schlagwort oder Inhalt suchen"
              aria-label="Dokumente durchsuchen"
              className="h-11 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-10 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-900/40 dark:text-white dark:focus:border-primary-400"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                aria-label="Suche löschen"
                className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex rounded-lg bg-gray-100 p-1 dark:bg-gray-900/60" aria-label="Dokumentfilter">
              <button
                type="button"
                onClick={() => setFilter('available')}
                className={`min-h-9 rounded-md px-3 text-xs font-semibold transition sm:text-sm ${filter === 'available' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
              >
                Verfügbar <span className="ml-1 tabular-nums text-gray-400">{availableCount}</span>
              </button>
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`min-h-9 rounded-md px-3 text-xs font-semibold transition sm:text-sm ${filter === 'all' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
              >
                Alle <span className="ml-1 tabular-nums text-gray-400">{documents.length}</span>
              </button>
            </div>
            <button
              type="button"
              onClick={fetchDocuments}
              disabled={loading}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              title="Dokumente aktualisieren"
              aria-label="Dokumente aktualisieren"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            </button>
          </div>
        </section>

        <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_270px]">
          <section className="flex min-h-0 flex-col lg:border-r lg:border-gray-200 lg:dark:border-gray-700">
            <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 px-4 py-2.5 dark:border-gray-700/70 sm:px-5">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                {loading ? 'Dokumente werden geladen' : `${filteredDocs.length} Dokument${filteredDocs.length === 1 ? '' : 'e'} gefunden`}
              </p>
              {filteredDocs.length > 0 && (
                <button type="button" onClick={toggleVisibleDocuments} className="text-xs font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
                  {allVisibleSelected ? 'Sichtbare abwählen' : 'Sichtbare auswählen'}
                </button>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5">
              {loading && documents.length === 0 ? (
                <div className="space-y-2" aria-label="Dokumente werden geladen">
                  {[0, 1, 2].map((item) => <div key={item} className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/60" />)}
                </div>
              ) : error ? (
                <div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
                  <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-error-50 text-error-600 dark:bg-error-900/20 dark:text-error-400"><ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" /></span>
                  <p className="font-semibold text-gray-900 dark:text-white">Dokumente nicht verfügbar</p>
                  <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">{error}</p>
                  <button type="button" onClick={fetchDocuments} className="mt-4 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">Erneut laden</button>
                </div>
              ) : filteredDocs.length === 0 ? (
                <div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
                  <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700"><DocumentIcon className="h-6 w-6" aria-hidden="true" /></span>
                  <p className="font-semibold text-gray-900 dark:text-white">Keine Dokumente gefunden</p>
                  <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">{searchTerm ? 'Passe den Suchbegriff oder den Filter an.' : 'Aktuell stehen keine unverknüpften Dokumente zur Verfügung.'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredDocs.map((doc) => {
                    const selected = selectedDocuments.has(doc.id);
                    const linked = isLinked(doc);
                    const currentlyLinked = preSelectedIds.has(doc.id);
                    const exactAmount = doc.invoice_amount != null && Math.abs(Number(doc.invoice_amount) - targetAmount) < 0.01;
                    const fileSize = formatFileSize(doc.size);

                    return (
                      <button
                        key={doc.id}
                        type="button"
                        role="checkbox"
                        aria-checked={selected}
                        onClick={() => toggleDocument(doc.id)}
                        className={`group w-full rounded-xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${selected ? 'border-primary-400 bg-primary-50/70 shadow-sm dark:border-primary-600 dark:bg-primary-900/20' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700/70'}`}
                      >
                        <div className="flex min-w-0 items-start gap-3">
                          <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${selected ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>{getDocumentIcon(doc)}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{doc.name}</p>
                                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  <span>{documentType(doc)}</span>
                                  {fileSize && <span>{fileSize}</span>}
                                  <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" aria-hidden="true" />{formatDate(doc.created_at)}</span>
                                </p>
                              </div>
                              <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 transition ${selected ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-transparent group-hover:border-primary-400 dark:border-gray-600 dark:bg-gray-800'}`} aria-hidden="true"><CheckIcon className="h-4 w-4" /></span>
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                              {doc.invoice_amount != null && (
                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-bold tabular-nums ${exactAmount ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                                  {formatCurrency(Number(doc.invoice_amount))}{exactAmount && <span className="ml-1 font-semibold">· passend</span>}
                                </span>
                              )}
                              {currentlyLinked ? (
                                <span className="inline-flex items-center gap-1 rounded-md bg-primary-100 px-2 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"><LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />Aktuell verknüpft</span>
                              ) : linked ? (
                                <span className="inline-flex items-center gap-1 rounded-md bg-warning-50 px-2 py-1 text-xs font-semibold text-warning-700 dark:bg-warning-900/20 dark:text-warning-300"><LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />Bereits verknüpft</span>
                              ) : null}
                              {doc.member_name && <span className="inline-flex min-w-0 items-center gap-1 text-xs text-gray-500 dark:text-gray-400"><UserIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" /><span className="max-w-44 truncate">{doc.member_name}</span></span>}
                              {doc.tag && <span className="inline-flex min-w-0 items-center gap-1 text-xs text-gray-500 dark:text-gray-400"><TagIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" /><span className="max-w-36 truncate">{doc.tag}</span></span>}
                            </div>

                            {doc.ai_comment && <p className="mt-2 line-clamp-2 border-l-2 border-gray-200 pl-2 text-xs leading-relaxed text-gray-500 dark:border-gray-600 dark:text-gray-400">{doc.ai_comment}</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <aside className="hidden min-h-0 flex-col bg-gray-50/70 dark:bg-gray-900/25 lg:flex">
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Ihre Auswahl</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{selectedDocs.length === 0 ? 'Noch kein Dokument ausgewählt' : `${selectedDocs.length} ${selectedDocs.length === 1 ? 'Dokument' : 'Dokumente'}`}</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {selectedDocs.length === 0 ? (
                <div className="flex h-full min-h-48 flex-col items-center justify-center px-4 text-center text-gray-400"><PaperClipIcon className="h-7 w-7" aria-hidden="true" /><p className="mt-2 text-xs leading-relaxed">Wähle links die Belege aus, die zu dieser Buchung gehören.</p></div>
              ) : (
                <div className="space-y-2">
                  {selectedDocs.map((doc) => (
                    <div key={doc.id} className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800">
                      <span className="mt-0.5 text-gray-400">{getDocumentIcon(doc)}</span>
                      <div className="min-w-0 flex-1"><p className="truncate text-xs font-semibold text-gray-900 dark:text-white">{doc.name}</p><p className="mt-0.5 text-xs font-bold tabular-nums text-gray-600 dark:text-gray-300">{doc.invoice_amount != null ? formatCurrency(Number(doc.invoice_amount)) : 'Kein Betrag'}</p></div>
                      <button type="button" onClick={() => toggleDocument(doc.id)} aria-label={`${doc.name} aus Auswahl entfernen`} className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white"><XMarkIcon className="h-4 w-4" aria-hidden="true" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 p-4 dark:border-gray-700">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3 text-gray-600 dark:text-gray-400"><dt>Dokumente</dt><dd className="font-semibold tabular-nums text-gray-900 dark:text-white">{selectedDocs.length}</dd></div>
                <div className="flex justify-between gap-3 text-gray-600 dark:text-gray-400"><dt>Belegsumme</dt><dd className="font-semibold tabular-nums text-gray-900 dark:text-white">{formatCurrency(selectedAmount)}</dd></div>
                <div className={`flex justify-between gap-3 border-t pt-2 font-bold ${amountMatches ? 'border-success-200 text-success-700 dark:border-success-800 dark:text-success-400' : 'border-gray-200 text-gray-900 dark:border-gray-700 dark:text-white'}`}><dt>Differenz</dt><dd className="tabular-nums">{formatCurrency(difference)}</dd></div>
              </dl>
              <p className="mt-3 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">Die Auswahl ersetzt die aktuell mit dieser Buchung verknüpften Dokumente.</p>
            </div>
          </aside>
        </div>

        {actionError && (
          <div role="alert" className="flex flex-shrink-0 items-start gap-2 border-t border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300 sm:px-5"><ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" /><span>{actionError}</span></div>
        )}
      </div>
    </ModalShell>
  );
};

export default InvoiceMatcher;
